// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminLeveraged.js
// Admin: leveraged/inverse ETF families and underlyings.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

// ---------------------------------------------------------------------------
// Styles (follows AdminEtf.js)
// ---------------------------------------------------------------------------
const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  nav: {
    background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    padding: '0 32px', height: '56px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  logo:    { fontFamily: "'DM Mono', monospace", fontSize: '13px', fontWeight: 500, letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase' },
  navRight:{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", padding: '6px 12px', borderRadius: 'var(--radius)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '32px' },
  tabs:    { display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid var(--border)' },
  tab:     (active) => ({
    padding: '8px 18px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
    background: 'none', border: 'none', fontFamily: "'DM Sans', sans-serif",
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    marginBottom: '-1px',
  }),
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:      { padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  btn:     { padding: '5px 11px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" },
  input:   { padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '12px', fontFamily: "'DM Mono', monospace", background: 'var(--bg)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' },
  chip: (long) => ({
    display: 'inline-block', padding: '2px 8px', marginRight: '5px', borderRadius: '4px',
    fontFamily: "'DM Mono', monospace", fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
    background: long ? '#f0fdf4' : '#fee2e2', color: long ? '#15803d' : '#991b1b',
  }),
  uChip: {
    display: 'inline-block', padding: '2px 10px', borderRadius: '4px',
    fontFamily: "'DM Mono', monospace", fontSize: '11px', fontWeight: 700,
    background: 'var(--accent-dim)', color: 'var(--accent)', whiteSpace: 'nowrap',
  },
  err:  { color: 'var(--error)',   fontSize: '13px', marginTop: '8px' },
  ok:   { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  filterBar: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  searchInput: { padding: '7px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none', width: '240px' },
};

function Btn({ onClick, color = 'var(--accent)', textColor = '#fff', children, disabled, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...s.btn, background: color, color: textColor, opacity: disabled ? 0.5 : 1, ...style }}>
      {children}
    </button>
  );
}

function ratioTag(r) {
  const n = parseFloat(r);
  return `${n % 1 === 0 ? n.toFixed(0) : n}x`;
}

// ---------------------------------------------------------------------------
// Editable member row (edit inside expanded family, or add from Unlinked)
// ---------------------------------------------------------------------------
function LinkRow({ link, isNew, onSaved, flash }) {
  const [label, setLabel]   = useState(link.underlying_label || '');
  const [uSym, setUSym]     = useState(link.underlying_symbol || '');
  const [ratio, setRatio]   = useState(link.ratio != null ? String(link.ratio) : '');
  const [notes, setNotes]   = useState(link.notes || '');
  const [busy, setBusy]     = useState(false);

  const dirty = isNew
    || label !== (link.underlying_label || '')
    || uSym !== (link.underlying_symbol || '')
    || String(ratio) !== String(link.ratio)
    || notes !== (link.notes || '');

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/leveraged/admin/links/save', {
        instrument_id: link.instrument_id,
        underlying_label: label,
        underlying_symbol: uSym,
        ratio: parseFloat(ratio),
        notes,
      });
      flash(`${link.symbol} saved`);
      onSaved();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Remove ${link.symbol} from leveraged links?`)) return;
    setBusy(true);
    try {
      await api.post('/leveraged/admin/links/delete', { instrument_id: link.instrument_id });
      flash(`${link.symbol} removed`);
      onSaved();
    } catch (e) { flash(e.response?.data?.error || 'Failed', true); }
    finally { setBusy(false); }
  };

  return (
    <tr>
      <td style={{ ...s.td, fontFamily: "'DM Mono', monospace", fontWeight: 600, whiteSpace: 'nowrap' }}>{link.symbol}</td>
      <td style={{ ...s.td, color: 'var(--text-muted)', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={link.name}>{link.name}</td>
      <td style={s.td}>
        <input style={{ ...s.input, width: '200px' }} list="ll-labels" value={label}
          onChange={e => setLabel(e.target.value)} placeholder="Family label" />
      </td>
      <td style={s.td}>
        <input style={{ ...s.input, width: '80px' }} value={uSym}
          onChange={e => setUSym(e.target.value.toUpperCase())} placeholder="—" title={link.underlying_name || ''} />
      </td>
      <td style={s.td}>
        <input style={{ ...s.input, width: '60px' }} value={ratio}
          onChange={e => setRatio(e.target.value)} placeholder="2.00" />
      </td>
      <td style={s.td}>
        <input style={{ ...s.input, width: '240px', fontFamily: "'DM Sans', sans-serif" }} value={notes}
          onChange={e => setNotes(e.target.value)} placeholder="" />
      </td>
      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <Btn onClick={save} disabled={busy || !dirty || !label || !ratio}>{isNew ? 'Add' : 'Save'}</Btn>
          {!isNew && <Btn onClick={del} color="var(--surface)" textColor="var(--error)" disabled={busy}>Delete</Btn>}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Families tab — one row per family:  LONG | UNDERLYING | SHORT
// Click a row to expand and edit members.
// ---------------------------------------------------------------------------
function FamiliesTab({ links, reload, flash }) {
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null); // underlying_label

  const families = useMemo(() => {
    const m = new Map();
    links.forEach(l => {
      if (!m.has(l.underlying_label)) m.set(l.underlying_label, []);
      m.get(l.underlying_label).push(l);
    });
    return [...m.entries()].map(([label, rows]) => ({
      label,
      rows,
      underlying: rows.find(r => r.underlying_symbol)?.underlying_symbol || null,
      longs:  rows.filter(r => parseFloat(r.ratio) > 0).sort((a, b) => b.ratio - a.ratio),
      shorts: rows.filter(r => parseFloat(r.ratio) < 0).sort((a, b) => a.ratio - b.ratio),
    }));
  }, [links]);

  const filtered = families.filter(f =>
    !search || `${f.label} ${f.underlying || ''} ${f.rows.map(r => r.symbol).join(' ')}`
      .toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={s.filterBar}>
        <input style={s.searchInput} placeholder="Search label, symbol…" value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {filtered.length} of {families.length} families · {links.length} products · click a row to edit
        </span>
      </div>

      <div style={s.section}>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>{['Family','Long','Underlying','Short'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <React.Fragment key={f.label}>
                  <tr onClick={() => setExpanded(expanded === f.label ? null : f.label)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...s.td, fontFamily: "'DM Mono', monospace", fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {expanded === f.label ? '▾ ' : '▸ '}{f.label}
                    </td>
                    <td style={s.td}>
                      {f.longs.map(r => <span key={r.instrument_id} style={s.chip(true)} title={r.name}>{r.symbol} {ratioTag(r.ratio)}</span>)}
                      {f.longs.length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={s.td}>
                      {f.underlying ? <span style={s.uChip}>{f.underlying}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={s.td}>
                      {f.shorts.map(r => <span key={r.instrument_id} style={s.chip(false)} title={r.name}>{r.symbol} {ratioTag(r.ratio)}</span>)}
                      {f.shorts.length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </tr>
                  {expanded === f.label && (
                    <tr>
                      <td colSpan={4} style={{ ...s.td, padding: '0 0 12px 24px', background: 'var(--bg)' }}>
                        <table style={{ ...s.table, marginTop: '8px' }}>
                          <thead>
                            <tr>{['Symbol','Name','Family Label','Underlying','Ratio','Notes','Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {f.rows.map(l => (
                              <LinkRow key={l.instrument_id} link={l} onSaved={reload} flash={flash} />
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} style={{ ...s.td, color: 'var(--text-muted)', textAlign: 'center', padding: '32px' }}>No families match the current filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unlinked tab — geared US ETFs not yet in leveraged_links
// ---------------------------------------------------------------------------
function UnlinkedTab({ unlinked, reload, flash }) {
  const [search, setSearch] = useState('');

  const filtered = unlinked.filter(u =>
    !search || `${u.symbol} ${u.name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={s.filterBar}>
        <input style={s.searchInput} placeholder="Search symbol or name…" value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {filtered.length} of {unlinked.length} unlinked geared products
        </span>
      </div>

      <div style={s.section}>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>{['Symbol','Name','Family Label','Underlying','Ratio','Notes','Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <LinkRow key={u.instrument_id} isNew onSaved={reload} flash={flash}
                  link={{
                    instrument_id: u.instrument_id,
                    symbol: u.symbol,
                    name: u.name,
                    underlying_label: '',
                    underlying_symbol: '',
                    ratio: u.gearing_ratio != null ? (u.gearing_ratio / 100).toFixed(2) : '',
                    notes: '',
                  }} />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ ...s.td, color: 'var(--text-muted)', textAlign: 'center', padding: '32px' }}>Nothing unlinked.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function AdminLeveraged() {
  const { user } = useAuth();
  const navigate         = useNavigate();
  const [tab, setTab]    = useState('families');
  const [links, setLinks]       = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]    = useState('');
  const [err, setErr]    = useState('');

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, ur] = await Promise.all([
        api.get('/leveraged/admin/links'),
        api.get('/leveraged/admin/unlinked'),
      ]);
      setLinks(lr.data.links);
      setUnlinked(ur.data.unlinked);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const labels = useMemo(() => [...new Set(links.map(l => l.underlying_label))].sort(), [links]);


  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(''); } else { setMsg(m); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />

      <div style={s.content}>
        <h1 style={s.heading}>Leveraged Links</h1>
        <p style={s.sub}>Leveraged/inverse product families: long products | underlying | short products.</p>

        {msg && <div style={{ ...s.ok, marginBottom: '16px' }}>✓ {msg}</div>}
        {err && <div style={{ ...s.err, marginBottom: '16px' }}>✕ {err}</div>}

        {/* shared label suggestions for all label inputs */}
        <datalist id="ll-labels">
          {labels.map(l => <option key={l} value={l} />)}
        </datalist>

        <div style={s.tabs}>
          {[['families', `Families (${links.length})`], ['unlinked', `Unlinked (${unlinked.length})`]].map(([key, label]) => (
            <button key={key} style={s.tab(tab === key)} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
        ) : tab === 'families' ? (
          <FamiliesTab links={links} reload={load} flash={flash} />
        ) : (
          <UnlinkedTab unlinked={unlinked} reload={load} flash={flash} />
        )}
      </div>
    </div>
  );
}
